import React, { useState } from 'react';
import { ShowModal } from 'src/components/Modal';

export type RefundStatus = 'PENDENTE' | 'APROVADA' | 'REPROVADA';

export interface RefundRequestItem {
  orderId: string;
  sellerId: string;
  id: string;
  orderNumber: string;
  userName: string;
  sellerName: string;
  orderValue: number;
  orderDate: string;
  createdAt: string;
  status?: RefundStatus;
  sellerStatus?: RefundStatus;
  motive?: string;
  images?: { imageUrl: string }[];
}

export interface RefundRequestsUIProps {
  items: RefundRequestItem[];
  loading: boolean;
  onApprove: (id: string, sellerId: string) => void;
}

const Currency: React.FC<{ value: number }> = ({ value }) => (
  <span>
    {new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)}
  </span>
);

const Badge: React.FC<{ value?: RefundStatus }> = ({ value }) => {
  const v = value ?? 'PENDENTE';
  const cls =
    v === 'APROVADA'
      ? 'bg-emerald-600'
      : v === 'REPROVADA'
        ? 'bg-red-500'
        : 'bg-amber-500';
  return (
    <span className={`rounded-full px-2 py-1 text-xs text-white ${cls}`}>
      {v}
    </span>
  );
};

const Line: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="grid grid-cols-3 gap-2 py-1 text-sm">
    <div className="text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value ?? '-'}</div>
  </div>
);

const RefundRequestsUI: React.FC<RefundRequestsUIProps> = ({
  items,
  loading,
  onApprove,
}) => {
  const [idToApprove, setIdToApprove] = useState<{
    id: string;
    sellerId: string;
  } | null>(null);
  const [detailsItem, setDetailsItem] = useState<RefundRequestItem | null>(
    null
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <div className="w-full p-4">
      <h2 className="mb-4 text-xl font-semibold">Solicitações de Reembolso</h2>

      <div className="sm:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border p-4 text-center text-gray-500">
            Carregando.
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border p-4 text-center text-gray-500">
            Nenhum registro encontrado
          </div>
        )}
        {!loading &&
          items.map((r) => (
            <div key={r.id} className="rounded-xl border p-4">
              <div className="flex justify-between text-sm">
                <div className="font-medium">{r.orderNumber}</div>
                <div className="text-gray-600">{r.orderDate}</div>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-1 text-sm">
                <div className="truncate">
                  <span className="text-gray-600">Usuário: </span>
                  {r.userName}
                </div>
                <div className="truncate">
                  <span className="text-gray-600">Seller: </span>
                  {r.sellerName}
                </div>
                <div>
                  <span className="text-gray-600">Valor: </span>
                  <Currency value={r.orderValue} />
                </div>
                <div className="flex items-center gap-2">
                  <Badge value={r.status} />
                  <span className="text-gray-600">Seller:</span>
                  <Badge value={r.sellerStatus} />
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setDetailsItem(r);
                    setIsDetailsOpen(true);
                  }}
                  className="rounded-md border px-3 py-1 text-xs"
                >
                  Detalhes
                </button>
                <button
                  onClick={() =>
                    setIdToApprove({ id: r.orderId, sellerId: r.sellerId })
                  }
                  className="rounded-md bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-50"
                  disabled={r.sellerStatus === 'APROVADA'}
                >
                  Aprovar
                </button>
              </div>
            </div>
          ))}
      </div>

      <div className="hidden sm:block">
        <div className="overflow-x-auto rounded-xl border">
          <table className="min-w-[920px] table-auto text-left text-sm">
            <thead className="bg-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-3">Pedido</th>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Seller</th>
                <th className="px-4 py-3">Valor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Status (Seller)</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    Carregando.
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    Nenhum registro encontrado
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((r) => (
                  <tr key={r.id} className="align-middle">
                    <td className="px-4 py-2">{r.orderNumber}</td>
                    <td
                      className="px-4 py-2 max-w-[240px] truncate"
                      title={r.userName}
                    >
                      {r.userName}
                    </td>
                    <td
                      className="px-4 py-2 max-w-[240px] truncate"
                      title={r.sellerName}
                    >
                      {r.sellerName}
                    </td>
                    <td className="px-4 py-2">
                      <Currency value={r.orderValue} />
                    </td>
                    <td className="px-4 py-2">
                      <Badge value={r.status} />
                    </td>
                    <td className="px-4 py-2">
                      <Badge value={r.sellerStatus} />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setDetailsItem(r);
                            setIsDetailsOpen(true);
                          }}
                          className="rounded-md border px-3 py-1 text-xs"
                        >
                          Detalhes
                        </button>
                        <button
                          onClick={() =>
                            setIdToApprove({
                              id: r.orderId,
                              sellerId: r.sellerId,
                            })
                          }
                          className="rounded-md bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-50"
                          disabled={r.sellerStatus === 'APROVADA'}
                        >
                          Aprovar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {idToApprove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
            <h3 className="mb-3 font-semibold">Confirmar aprovação</h3>
            <p className="mb-4 text-sm text-gray-600">
              Deseja aprovar esta solicitação de reembolso?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIdToApprove(null)}
                className="rounded-md border px-3 py-1 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  onApprove(idToApprove.id, idToApprove.sellerId);
                  setIdToApprove(null);
                }}
                className="rounded-md bg-emerald-600 px-3 py-1 text-sm text-white"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      <ShowModal
        visible={isDetailsOpen}
        onSetVisible={setIsDetailsOpen}
        title={
          detailsItem ? `Detalhes • ${detailsItem.orderNumber}` : 'Detalhes'
        }
        showCloseButton
      >
        {detailsItem && (
          <div className="space-y-4">
            <div className="rounded-xl border p-3">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-2">
                Reembolso
              </div>
              <Line label="ID" value={detailsItem.id} />
              <Line
                label="Status (Plataforma)"
                value={<Badge value={detailsItem.status} />}
              />
              <Line
                label="Status (Seller)"
                value={<Badge value={detailsItem.sellerStatus} />}
              />
              <Line label="Criado em" value={detailsItem.createdAt} />
              <Line label="Motivo" value={detailsItem.motive || '-'} />
            </div>
            <div className="rounded-xl border p-3">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-2">
                Pedido
              </div>
              <Line label="Número" value={detailsItem.orderNumber} />
              <Line label="Data do pedido" value={detailsItem.orderDate} />
              <Line
                label="Valor total"
                value={<Currency value={detailsItem.orderValue} />}
              />
            </div>
            <div className="rounded-xl border p-3">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-2">
                Partes
              </div>
              <Line label="Usuário" value={detailsItem.userName} />
              <Line label="Seller" value={detailsItem.sellerName} />
              <Line label="Seller ID" value={detailsItem.sellerId} />
            </div>

            {detailsItem?.images?.length ? (
              <div className="mt-4 space-y-2">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Imagens
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {detailsItem.images.map(
                    (img: { imageUrl: string }, i: number) => (
                      <a
                        key={i}
                        href={`${process.env.REACT_APP_BASE_URL}/uploads/${img.imageUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block overflow-hidden rounded-md border border-gray-200 hover:border-gray-300 hover:opacity-90"
                      >
                        <img
                          src={`${process.env.REACT_APP_BASE_URL}/uploads/${img.imageUrl}`}
                          alt={`Imagem ${i + 1}`}
                          className="w-full h-40 object-cover bg-gray-50"
                          loading="lazy"
                        />
                      </a>
                    )
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </ShowModal>
    </div>
  );
};

export default RefundRequestsUI;
